import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { CustomerDraft } from "~fulfill/customers/customer/helpers/customerDraft";
import type { ShipToCheckpoint } from "~fulfill/customers/customer/helpers/locationDraft";
import type { InspectedCustomerGroup } from "~fulfill/customers/customerGroups/hooks/useCustomerGroupInspectionSource";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import isRecord from "~/helpers/isRecord";
import { isCustomerDraft } from "~fulfill/customers/customer/helpers/customerDraft";
import { CUSTOMERS_BASE_PATH } from "~fulfill/customers/customer/helpers/customerGroupMembersScope";
import isShipToCheckpoint from "~fulfill/customers/customer/helpers/isShipToCheckpoint";
import resolveCustomerTab, { DEFAULT_CUSTOMER_TAB } from "~fulfill/customers/customer/helpers/resolveCustomerTab";

export const CUSTOMER_TARGET_KIND = "customer";

export const NEW_CUSTOMER_TARGET_KIND = "newCustomer";

export const CUSTOMER_SOURCE_KIND = "customer";

export const CUSTOMER_GROUP_CUSTOMER_SOURCE_KIND = "customerGroupOnCustomer";

export interface CustomerSourceCheckpoint {
	customerId: string;
	customerName: string;
	draft: CustomerDraft;
	inspectedCustomerGroup?: InspectedCustomerGroup;
	shipTo?: ShipToCheckpoint;
	tab?: string;
}

export const isCustomerSourceCheckpoint = (value: unknown): value is CustomerSourceCheckpoint =>
	isRecord(value) &&
	typeof value.customerId === "string" &&
	typeof value.customerName === "string" &&
	isCustomerDraft(value.draft) &&
	(value.shipTo === undefined || isShipToCheckpoint(value.shipTo)) &&
	(value.tab === undefined || (typeof value.tab === "string" && resolveCustomerTab(value.tab) === value.tab)) &&
	(value.inspectedCustomerGroup === undefined ||
		(isRecord(value.inspectedCustomerGroup) &&
			typeof value.inspectedCustomerGroup.customerGroupId === "string" &&
			typeof value.inspectedCustomerGroup.name === "string"));

export const isCustomerGroupCustomerSourceCheckpoint = (
	value: unknown
): value is CustomerSourceCheckpoint & { inspectedCustomerGroup: InspectedCustomerGroup } =>
	isCustomerSourceCheckpoint(value) && value.inspectedCustomerGroup !== undefined;

export const customerEditorKey = (customerId: string): string => `customer:${customerId}`;

export const PRE_PRICING_TAB = "8";

const CUSTOMER_ROUTE_PATTERN = /^\/fulfill\/customers\/customer\/([^/?#]+)(?:\/[^/?#]+)?$/;

export const NEW_CUSTOMER_TARGET: ContinuationTarget = { targetId: "new", targetKind: NEW_CUSTOMER_TARGET_KIND };

export const buildCustomerTarget = (customerId: string, listFilters?: Record<string, string>): ContinuationTarget => ({
	listFilters,
	targetId: customerId,
	targetKind: CUSTOMER_TARGET_KIND
});

export const buildPrePricingTarget = (customerId: string, itemId: string): ContinuationTarget =>
	buildCustomerTarget(customerId, { itemId, tab: PRE_PRICING_TAB });

export const buildCustomerPath = (customerId: string, tab?: string): string => {
	const resolvedTab = resolveCustomerTab(tab);
	const base = `${CUSTOMERS_BASE_PATH}/customer/${encodeURIComponent(customerId)}`;
	return resolvedTab === DEFAULT_CUSTOMER_TAB ? base : `${base}/${resolvedTab}`;
};

export const matchCustomerReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = CUSTOMER_ROUTE_PATTERN.exec(pathname);
	return match ? { targetId: decodeURIComponent(match[1]), targetKind: CUSTOMER_TARGET_KIND } : null;
};

const customerReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ listFilters, targetId }) => buildCustomerPath(targetId, listFilters?.tab),
	matchesRoute: matchCustomerReceiverRoute
};

export const matchNewCustomerReceiverRoute = (pathname: string): ContinuationTarget | null =>
	pathname === CUSTOMERS_BASE_PATH ? NEW_CUSTOMER_TARGET : null;

export const newCustomerReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: () => CUSTOMERS_BASE_PATH,
	matchesRoute: matchNewCustomerReceiverRoute
};

export default customerReceiverIdentityAdapter;
